import React, { Fragment } from 'react';
import classNames from 'classnames';
import { Text } from '@dropbox/dig-components/typography';
import { IconButton } from '@dropbox/dig-components/buttons';
import { Tooltip } from '@dropbox/dig-components/tooltips';
import { UIIcon } from '@dropbox/dig-icons';
import { InfoLine } from '@dropbox/dig-icons/assets';
import { defineMessages, useIntl } from 'react-intl';
import styles from './index.scss';
import FeatureValue from './feature-value';
import type { FeatureRow } from './helpers';
import type { PlanColumn } from './use-pricing-plan-columns';

const messages = defineMessages({
  planFeatureComparison: {
    id: '',
    description: 'Accessible label for the plan feature comparison grid',
    defaultMessage: 'Plan feature comparison',
  },
  featureInfo: {
    id: '',
    description: 'Accessible label for a pricing feature information button',
    defaultMessage: 'About {feature}',
  },
});

export interface PricingFeatureGridProps {
  planColumns: PlanColumn[];
  featureRows: FeatureRow[];
  featuresSectionTitle: React.ReactNode;
  className?: string;
  /** When true, render as fragment so rows slot into the parent pricing grid (desktop). */
  embeddedInParentGrid?: boolean;
}

const PricingFeatureGrid: React.FC<PricingFeatureGridProps> = ({
  planColumns,
  featureRows,
  featuresSectionTitle,
  className,
  embeddedInParentGrid = false,
}) => {
  const intl = useIntl();
  const rows = featureRows.map((row, rowIndex) => (
    <div
      key={row.id}
      className={styles.featureRow}
      role="row"
      data-last-row={rowIndex === featureRows.length - 1}
    >
      <Text
        variant="label"
        size="small"
        tagName="div"
        className={styles.featureLabel}
        role="rowheader"
      >
        <span className={styles.featureLabelText}>{row.label}</span>
        {row.tooltip && (
          <Tooltip
            title={row.tooltip}
            placement="top"
            auto
            shouldCloseOnClick={false}
          >
            <IconButton
              type="button"
              variant="transparent"
              size="small"
              className={styles.featureTooltipTrigger}
              aria-label={intl.formatMessage(messages.featureInfo, {
                feature: row.label,
              })}
            >
              <UIIcon src={InfoLine} size="small" />
            </IconButton>
          </Tooltip>
        )}
      </Text>
      {planColumns.map(({ plan, planIndex, columnKey, columnSlot }) => (
        <div
          key={`${row.id}-${columnKey}`}
          className={styles.featureValue}
          data-plan-index={planIndex}
          data-column-slot={columnSlot}
          role="cell"
        >
          <FeatureValue row={row} plan={plan} />
        </div>
      ))}
    </div>
  ));

  if (embeddedInParentGrid) {
    return (
      <Fragment>
        {featuresSectionTitle}
        {rows}
      </Fragment>
    );
  }

  return (
    <div
      className={classNames(className)}
      role="grid"
      aria-label={intl.formatMessage(messages.planFeatureComparison)}
    >
      {featuresSectionTitle}
      {rows}
    </div>
  );
};

export default PricingFeatureGrid;
